import { useState } from 'react';
import { X } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
  'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
  'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab',
  'Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh',
  'Uttarakhand','West Bengal','Delhi','Jammu & Kashmir','Ladakh','Puducherry',
];

const EMPTY = { name: '', phone: '', email: '', address_line1: '', address_line2: '', city: '', state: 'Tamil Nadu', pincode: '', is_default: false };

// Add / edit a saved address. Pass `address` to edit, omit to add new.
export default function AddressFormModal({ address, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, ...(address || {}) });
  const [saving, setSaving] = useState(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const validate = () => {
    if (!form.name.trim()) return 'Full name is required';
    if (!/^\d{10}$/.test(String(form.phone).trim())) return 'Enter a valid 10-digit phone';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form.email).trim())) return 'Enter a valid email';
    if (!form.address_line1.trim()) return 'Address is required';
    if (!form.city.trim()) return 'City is required';
    if (!form.state) return 'State is required';
    if (!/^\d{6}$/.test(String(form.pincode).trim())) return 'Enter a valid 6-digit pincode';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), phone: String(form.phone).trim(), email: String(form.email).trim(),
        address_line1: form.address_line1.trim(), address_line2: form.address_line2?.trim() || '',
        city: form.city.trim(), state: form.state, pincode: String(form.pincode).trim(),
        is_default: !!form.is_default,
      };
      const { data } = address?.id
        ? await api.put(`/addresses/${address.id}`, payload)
        : await api.post('/addresses', payload);
      toast.success(address?.id ? 'Address updated' : 'Address added');
      onSaved?.(data.address);
    } catch {
      toast.error('Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  const input = 'w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-brand-yellow';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-brand-dark">{address?.id ? 'Edit Address' : 'Add New Address'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input className={input} value={form.name} onChange={e => f('name', e.target.value)} placeholder="Full name" />
            <input className={input} value={form.phone} onChange={e => f('phone', e.target.value.replace(/\D/g, ''))} placeholder="10-digit phone" maxLength={10} />
          </div>
          <input className={input} type="email" value={form.email} onChange={e => f('email', e.target.value)} placeholder="Email (order confirmation will be sent here)" />
          <input className={input} value={form.address_line1} onChange={e => f('address_line1', e.target.value)} placeholder="House / Flat / Street" />
          <input className={input} value={form.address_line2} onChange={e => f('address_line2', e.target.value)} placeholder="Area / Landmark (optional)" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input className={input} value={form.city} onChange={e => f('city', e.target.value)} placeholder="City" />
            <select className={input} value={form.state} onChange={e => f('state', e.target.value)}>
              {STATES.map(s => <option key={s}>{s}</option>)}
            </select>
            <input className={input} value={form.pincode} onChange={e => f('pincode', e.target.value.replace(/\D/g, ''))} placeholder="Pincode" maxLength={6} />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={!!form.is_default} onChange={e => f('is_default', e.target.checked)} className="w-4 h-4 accent-brand-yellow" />
            Set as default address
          </label>
          <button onClick={handleSave} disabled={saving}
            className="btn-primary w-full py-3 rounded-xl font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
            {saving && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {address?.id ? 'Save Changes' : 'Add Address'}
          </button>
        </div>
      </div>
    </div>
  );
}
